import { html, type TemplateResult } from "lit-html";
import type { ClientState, Enrichment, LeadFields } from "./types";

export function renderSidebar(opts: {
  lead: LeadFields;
  state: ClientState;
  enrichment: Enrichment | null;
  pandOpen: boolean;
  onTogglePand: () => void;
  onCollapse: () => void;
}): TemplateResult {
  const { lead, state, enrichment, pandOpen, onTogglePand, onCollapse } = opts;
  const hasKlant = lead.naam || lead.locatie || lead.email || lead.telefoon || state.label_doel;

  return html`
    <aside class="mxch-sidebar">
      <div class="mxch-sidebar-head">
        <span>Live info</span>
        <button class="mxch-collapse" @click=${onCollapse} title="Sidebar inklappen">↤</button>
      </div>

      <section class="mxch-sec">
        <h4>Klant</h4>
        ${hasKlant
          ? html`
              ${lead.naam ? html`<div class="mxch-line">${lead.naam}</div>` : ""}
              ${lead.locatie ? html`<div class="mxch-line">${lead.locatie}</div>` : ""}
              ${lead.email ? html`<div class="mxch-line mxch-muted">${lead.email}</div>` : ""}
              ${lead.telefoon ? html`<div class="mxch-line mxch-muted">${lead.telefoon}</div>` : ""}
              ${state.label_doel ? html`<div class="mxch-doel">🎯 ${state.label_doel}</div>` : ""}
            `
          : html`<div class="mxch-empty">Claudio leert je gaandeweg kennen — hier verschijnt zo info.</div>`}
      </section>

      ${state.wensen.length > 0
        ? html`
            <section class="mxch-sec">
              <h4>Wensen</h4>
              <div class="mxch-tags">
                ${state.wensen.map((w) => html`<span class="mxch-tag">${w}</span>`)}
              </div>
            </section>
          `
        : ""}

      ${state.taken.length > 0
        ? html`
            <section class="mxch-sec">
              <h4>Taken</h4>
              <ul class="mxch-tasks">
                ${state.taken.map(
                  (t) => html`
                    <li class="${t.done ? "mxch-task-done" : ""}">
                      <span class="mxch-check">${t.done ? "✓" : "☐"}</span> ${t.label}
                    </li>
                  `,
                )}
              </ul>
            </section>
          `
        : ""}

      ${enrichment
        ? html`
            <section class="mxch-sec mxch-pand">
              <h4 @click=${onTogglePand} class="mxch-clickable">
                Pand-info ${pandOpen ? "▾" : "▸"}
              </h4>
              ${pandOpen ? renderPandInfo(enrichment) : ""}
            </section>
          `
        : ""}
    </aside>
  `;
}

function renderPandInfo(e: Enrichment): TemplateResult {
  const errors = Object.keys(e.errors ?? {}).filter((k) => k !== "energielabel");
  const labelMissing = e.errors?.energielabel === "geen label geregistreerd";
  // Altum kan label/bouwjaar/oppervlakte ook teruggeven als BAG/EP-online het niet heeft
  // deno-lint-ignore no-explicit-any
  const altum = e.altum as any;
  const labelText = e.energielabel?.huidig_label ?? altum?.energielabel ?? null;
  const bouwjaar = e.bag?.bouwjaar ?? altum?.bouwjaar ?? null;
  const oppervlakte = e.bag?.oppervlakte_m2 ?? altum?.oppervlakte_m2 ?? null;
  const woningtype = e.bag?.gebruiksdoel ?? altum?.woningtype ?? null;
  const waardeMin = altum?.waarde_min;
  const waardeMax = altum?.waarde_max;
  const waardeIndicatie = altum?.waarde_indicatie;

  return html`
    ${bouwjaar ? html`<div class="mxch-row"><span>Bouwjaar:</span><b>${bouwjaar}</b></div>` : ""}
    ${oppervlakte ? html`<div class="mxch-row"><span>Oppervlak:</span><b>${oppervlakte} m²</b></div>` : ""}
    ${woningtype ? html`<div class="mxch-row"><span>Type:</span><b>${woningtype}</b></div>` : ""}
    ${labelText
      ? html`<div class="mxch-row"><span>Label:</span><b class="mxch-label-${labelText}">${labelText}</b></div>`
      : labelMissing
        ? html`<div class="mxch-row"><span>Label:</span><b>geen geregistreerd</b></div>`
        : ""}
    ${e.energielabel?.geldig_tot
      ? html`<div class="mxch-row"><span>Label geldig tot:</span><b>${e.energielabel.geldig_tot.slice(0, 10)}</b></div>`
      : ""}
    ${waardeMin && waardeMax
      ? html`<div class="mxch-row"><span>Marktwaarde:</span><b>€${(waardeMin / 1000).toFixed(0)}K – €${(waardeMax / 1000).toFixed(0)}K</b></div>`
      : waardeIndicatie
        ? html`<div class="mxch-row"><span>Marktwaarde:</span><b>€${(waardeIndicatie / 1000).toFixed(0)}K</b></div>`
        : ""}
    ${e.geocode?.formatted
      ? html`<div class="mxch-row mxch-muted"><span>Adres:</span><b>${e.geocode.formatted}</b></div>`
      : ""}
    ${errors.length > 0 ? html`<div class="mxch-errors">⚠ ${errors.join(", ")}: tijdelijk niet bereikbaar</div>` : ""}
  `;
}
